import { useEffect, useRef, useState } from "react";
import ResizeObserver from "resize-observer-polyfill";

export type HTMLElementType =
  | HTMLHeadingElement
  | HTMLSpanElement
  | null
  | undefined;

export const useIsOverflown = () => {
  const [isOverflown, setIsOverflown] = useState<boolean | boolean[]>(false);
  const elementsRef = useRef<HTMLElementType | HTMLElementType[]>(null);

  const observeElement = (el: HTMLElementType) => {
    elementsRef.current = el;
    checkIfElementIsOverflown(el);
  };

  const observeElements = (els: HTMLElementType[]) => {
    elementsRef.current = els;
    checkIfElementsAreOverflown(els);
  };

  const checkIfElementIsOverflown = (element: HTMLElementType) => {
    const isOverflow = element
      ? element.offsetWidth < element.scrollWidth ||
        element.offsetHeight < element.scrollHeight
      : false;
    setIsOverflown(isOverflow);
  };

  const checkIfElementsAreOverflown = (elements: HTMLElementType[]) => {
    const overflowStatuses = elements.map((element) => {
      return element
        ? element.offsetWidth < element.scrollWidth ||
            element.offsetHeight < element.scrollHeight
        : false;
    });
    setIsOverflown(overflowStatuses);
  };

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (Array.isArray(elementsRef.current)) {
        checkIfElementsAreOverflown(elementsRef.current);
      } else if (elementsRef.current) {
        checkIfElementIsOverflown(elementsRef.current);
      }
    });

    if (Array.isArray(elementsRef.current)) {
      elementsRef.current.forEach((element) => {
        if (element) resizeObserver.observe(element);
      });
    } else if (elementsRef.current) {
      resizeObserver.observe(elementsRef.current);
    }

    return () => resizeObserver.disconnect();
    // eslint-disable-next-line
  }, [elementsRef.current]);

  return { observeElement, observeElements, isOverflown };
};
